import {
  ChangeDetectorRef,
  Component,
  computed,
  effect,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  ViewEncapsulation,
  ChangeDetectionStrategy,
} from '@angular/core';
import { FormArray, FormControl, FormGroup, ReactiveFormsModule, Validators, AbstractControl } from '@angular/forms';
import { CIPXQuoteRequestIntakeFormOwner, IntakeFormStep } from './intake-form.types';
import { CommonModule } from '@angular/common';
import { CIPXQuoteRequestIntakeFormBondLookupComponent } from './steps/intake-form-bond-lookup/intake-form-bond-lookup.component';
import { CIPXQuoteRequestIntakeFormRequestDetailsComponent } from './steps/intake-form-request-details/intake-form-request-details.component';
import { CIPXQuoteRequestIntakeFormAdditionalQuestionsComponent } from './steps/intake-form-additional-questions/intake-form-additional-questions.component';
import { CIPXQuoteRequestIntakeFormPrincipalSelectionComponent } from './steps/intake-form-principal-selection/intake-form-principal-selection.component';
import { CIPXQuoteRequestIntakeFormService } from './intake-form.service';
import { CathyService } from '@sbd-doug-frontend/app/services/cathy.service';
import { CIPXQuoteRequestIntakeFormConfirmationComponent } from './steps/intake-form-confirmation/intake-form-confirmation.component';
import { forceValidation } from '@sbd-doug-frontend/shared/lib/public-forms';
import { conditionalRequiredValidator } from '@sbd-doug-frontend/shared/lib/public-validators';
import { map, Subject, takeUntil } from 'rxjs';
import {
  configureFormValidationByStateBondRequirements,
  isCIPXQuoteRequestIntakeFormStateBond,
  requiresCredit,
} from './intake-form.helpers';
import { createQuoteRequestForm } from './intake-form';
import { BondLookupUIControlStage } from './steps/intake-form-bond-lookup/intake-form-bond-lookup.types';
import { findStageConfig, stages, updateStageConfig } from './steps/intake-form-bond-lookup/stage-config';
import { MatButtonModule } from '@angular/material/button';
import {
  CIPXScrollableContentComponent,
  CIPXActionBarComponent,
  CIPXCancelButtonComponent,
  CIPXPrimaryButtonComponent,
} from '@sbd-doug-frontend/shared/lib/ui';
import { CIPXQuoteRequestIntakeFormNotificationsComponent } from './steps/intake-form-notifications/intake-form-notifications.component';

@Component({
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    CIPXQuoteRequestIntakeFormPrincipalSelectionComponent,
    CIPXQuoteRequestIntakeFormBondLookupComponent,
    CIPXQuoteRequestIntakeFormRequestDetailsComponent,
    CIPXQuoteRequestIntakeFormConfirmationComponent,
    CIPXQuoteRequestIntakeFormAdditionalQuestionsComponent,
    CIPXQuoteRequestIntakeFormNotificationsComponent,
    MatButtonModule,
    CIPXScrollableContentComponent,
    CIPXActionBarComponent,
    CIPXCancelButtonComponent,
    CIPXPrimaryButtonComponent,
  ],
  selector: 'cipx-quote-request-intake-form',
  templateUrl: 'intake-form.component.html',
  styleUrl: 'intake-form.scss',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CIPXQuoteRequestIntakeFormComponent implements OnInit, OnDestroy {
  private _changeDetectorRef = inject(ChangeDetectorRef);
  private _intakeService = inject(CIPXQuoteRequestIntakeFormService);
  private _cathyService = inject(CathyService);
  private _unsubscribeAll: Subject<void> = new Subject<void>();
  @ViewChild('intakeFormScroll', { static: false, read: ElementRef }) intakeFormScroll!: ElementRef;

  @Input() stickyFooterActions = false;
  @Input() showCancelButton = false;
  IntakeFormStep = IntakeFormStep;

  currentStep = this._intakeService.currentStep;
  @Output() stepChanged = this._intakeService.stepChanged;
  @Output() complete = new EventEmitter<void>();
  @Output() cancelRequestQuote = new EventEmitter<void>();
  @Output() confirmationCloseRequested = new EventEmitter<void>();

  selectedStateBond = this._intakeService.selectedStateBond;

  quoteRequestForm = createQuoteRequestForm();

  // Loading state for quote request submission
  isRequestingQuote = false;
  validationAttemptedStep: IntakeFormStep | null = null;
  selectedBondLabel = computed(() => {
    const bondAmount = this.quoteRequestForm.get('bondLookup.bond_amount')?.value;
    const stateBond = this.selectedStateBond();

    if (bondAmount && stateBond) {
      const formattedAmount = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(bondAmount);
      if (stateBond.state_bond_id === 1) {
        return `${formattedAmount} - ${this.quoteRequestForm.get('bondLookup.bond_family_label')?.value}`;
      }
      return `${formattedAmount} ${stateBond.state_abbreviation} ${stateBond.surety_bond_label}`;
    } else if (stateBond) {
      return `${stateBond.state_abbreviation} ${stateBond.surety_bond_label}`;
    }
    return '';
  });

  // Used when the user clicks the "Change" button in the selected bond section
  moveBack() {
    switch (this.currentStep()) {
      case IntakeFormStep.RequestDetails:
      case IntakeFormStep.AdditionalQuestions:
      case IntakeFormStep.Notifications:
        this._intakeService.goToStep(IntakeFormStep.BondLookup);
        break;
      case IntakeFormStep.Confirmation: {
        this._intakeService.resetForm.emit();
        this._intakeService.goToStep(IntakeFormStep.BondLookup);
        const stateBondConfig = findStageConfig(BondLookupUIControlStage.BondSelectionCompleted, stages);
        if (stateBondConfig) {
          updateStageConfig(stateBondConfig, false);
        }
        break;
      }
    }
    this.scrollToTop();
  }

  constructor() {
    // Subscribe to state bond changes to update owner SSN validation
    effect(() => {
      const stateBond = this._intakeService.selectedStateBond();
      if (stateBond !== null) {
        // Refresh owner SSN validation when state bond changes
        setTimeout(() => {
          this.refreshOwnerSsnValidation();
        }, 0);
      }
    });

    // Keep dynamic additional_questions controls in sync with the currently selected state bond.
    // Without this, the UI can render questions from a "detailed" bond payload while the form still has an empty group.
    effect(() => {
      const stateBond = this._intakeService.selectedStateBond();
      if (stateBond) {
        configureFormValidationByStateBondRequirements(this.quoteRequestForm, stateBond);
      }
    });
  }

  ngOnInit() {
    this.initializeConditionalValidators();
    this.setupAddressValidationSubscription();

    this._intakeService.resetForm
      .pipe(
        takeUntil(this._unsubscribeAll),
        map(() => {
          this._intakeService.resetData();
          this.quoteRequestForm.reset();

          // Ensure owners are reinitialized to a single owner and related defaults set
          const ownersArray = this.quoteRequestForm.controls.requestDetails.controls.owners;
          while (ownersArray.length > 0) {
            ownersArray.removeAt(0);
          }
          this.addOwner();
          this.quoteRequestForm.controls.requestDetails.controls.company_number_owners.setValue(1);

          const contactsArray = this.quoteRequestForm.controls.communications.controls.contacts;
          while (contactsArray.length > 0) {
            contactsArray.removeAt(0);
          }

          // Reset company type default (Individual/Sole Proprietorship)
          this.quoteRequestForm.controls.bondLookup.controls.company_type_id.setValue(3);

          // Clear touched/dirty/errors across the form and refresh dynamic validation
          this.resetValidationState(this.quoteRequestForm);
          this.refreshOwnerSsnValidation();

          // Clear bond lookup selections and top-level bond fields
          const bondLookup = this.quoteRequestForm.controls.bondLookup;
          bondLookup.controls.stateSelection.setValue('');
          bondLookup.controls.bondFamilySelection.setValue('');
          bondLookup.controls.stateBondSelection.setValue('');
          bondLookup.controls.bond_family_label.setValue('');
          bondLookup.controls.surety_bond_user_notes.setValue('');
          bondLookup.controls.bond_amount.setValue(null);
          bondLookup.controls.selectedPenaltyOption.setValue(null);
          this.quoteRequestForm.controls.state_abbreviation.setValue('');
          this.quoteRequestForm.controls.state_bond_id.setValue(null);

          // Reset all bond lookup stages to defaults
          stages.forEach((stageConfig) => {
            updateStageConfig(stageConfig, stageConfig.defaultActive, true);
          });
        })
      )
      .subscribe();

    this._intakeService.companyTypeIdChanged
      .pipe(
        takeUntil(this._unsubscribeAll),
        map((value) => {
          const step = this.currentStep();
          if (
            ![IntakeFormStep.PrincipalSelection, IntakeFormStep.RequestDetails, IntakeFormStep.BondLookup].includes(
              step
            )
          ) {
            return value;
          }
          if (value === 3) {
            this.onCompanyNumberOwnersChanged(1);
          } else {
            this.quoteRequestForm.controls.requestDetails.controls.addCompanyAddress.setValue(true);
          }
          this._changeDetectorRef.detectChanges();
          return value;
        })
      )
      .subscribe();

    this._intakeService.companyNumberOwnersChanged
      .pipe(
        takeUntil(this._unsubscribeAll),
        map((value) => {
          const step = this.currentStep();
          if (
            ![IntakeFormStep.PrincipalSelection, IntakeFormStep.RequestDetails, IntakeFormStep.BondLookup].includes(
              step
            )
          ) {
            return value;
          }
          this.onCompanyNumberOwnersChanged(value);
          this._changeDetectorRef.detectChanges();
          return value;
        })
      )
      .subscribe();

    // Reset requesting state when leaving AdditionalQuestions
    this.stepChanged.pipe(takeUntil(this._unsubscribeAll)).subscribe((step) => {
      if (step !== IntakeFormStep.AdditionalQuestions && this.isRequestingQuote) {
        this.isRequestingQuote = false;
        this._changeDetectorRef.detectChanges();
      }
    });

    this.addOwner();
    // Ensure default owners/principal initialization happens only once at component init
  }

  ngOnDestroy(): void {
    this._unsubscribeAll.next();
    this._unsubscribeAll.complete();
  }

  initializeConditionalValidators(): void {
    const principalGroup = this.quoteRequestForm.controls.requestDetails.controls.principal;
    const requestDetailsGroup = this.quoteRequestForm.controls.requestDetails;

    if (principalGroup) {
      Object.keys(principalGroup.controls).forEach((key) => {
        // Apply conditional required only to certain address fields when addCompanyAddress is true
        const nonRequired = ['company_address_street_2', 'company_address_phone', 'company_address_county'];
        if (key.startsWith('company_address_') && nonRequired.includes(key) === false) {
          const control = principalGroup.get(key);
          if (control) {
            const currentValidators = control.validator ? [control.validator] : [];
            control.setValidators([
              ...currentValidators,
              // Apply the conditional validator using the requestDetails form group since that's where addCompanyAddress is
              conditionalRequiredValidator(['addCompanyAddress'], requestDetailsGroup),
            ]);
            // Update validity after setting new validators.
            control.updateValueAndValidity();
          }
        }
      });
    }
  }

  setupAddressValidationSubscription(): void {
    const addCompanyAddressControl = this.quoteRequestForm.controls.requestDetails.controls.addCompanyAddress;
    const principalGroup = this.quoteRequestForm.controls.requestDetails.controls.principal;

    // Subscribe to changes in the addCompanyAddress checkbox
    addCompanyAddressControl.valueChanges.pipe(takeUntil(this._unsubscribeAll)).subscribe(() => {
      // Re-validate all address fields when checkbox changes
      if (principalGroup) {
        Object.keys(principalGroup.controls).forEach((key) => {
          if (
            key.startsWith('company_address_') &&
            ['company_address_street_2', 'company_address_phone'].includes(key) === false
          ) {
            const control = principalGroup.get(key);
            if (control) {
              control.updateValueAndValidity();
            }
          }
        });
      }
    });
  }

  goToStep = this._intakeService.goToStep;

  onBondLookupComplete() {
    this.goToStep(IntakeFormStep.RequestDetails);
  }

  validateAndProceedToNext(currentStep: IntakeFormStep, nextStep: IntakeFormStep) {
    const isValid = this._intakeService.validateCurrentStep(
      currentStep,
      this.quoteRequestForm,
      this.selectedStateBond()
    );

    if (isValid) {
      this.validationAttemptedStep = null;
      this.scrollToTop();
      setTimeout(() => {
        this.goToStep(nextStep);
      }, 100);
    } else {
      this.validationAttemptedStep = currentStep;
      this._cathyService.error('Please fill in all required fields for this step.');
    }
  }

  shouldShowValidationErrors(step: IntakeFormStep): boolean {
    return this.validationAttemptedStep === step;
  }

  onSubmit() {
    // const reqDetails = this.quoteRequestForm.controls.requestDetails;
    // const nameCtrl = reqDetails.controls.principal.get('company_name');
    // const yearsCtrl = reqDetails.controls.company_years_in_business;
    // console.log('[Submit] BEFORE forceValidation requestDetails:', reqDetails.value);
    // console.log('[Submit] BEFORE forceValidation company_name=', nameCtrl?.value, 'status=', nameCtrl?.status, 'errors=', nameCtrl?.errors);
    // console.log('[Submit] BEFORE forceValidation company_years_in_business=', yearsCtrl?.value, 'status=', yearsCtrl?.status, 'errors=', yearsCtrl?.errors);

    forceValidation(this.quoteRequestForm);

    if (!this.quoteRequestForm.valid) {
      // console.log('[Submit] AFTER INVALID getFormErrors:', getFormErrors(this.quoteRequestForm));
      // console.log('[Submit] AFTER INVALID form.errors:', this.quoteRequestForm.errors);
      // console.log('[Submit] AFTER INVALID company_name=', nameCtrl?.value, 'status=', nameCtrl?.status, 'errors=', nameCtrl?.errors);
      // console.log('[Submit] AFTER INVALID company_years_in_business=', yearsCtrl?.value, 'status=', yearsCtrl?.status, 'errors=', yearsCtrl?.errors);
      this._cathyService.error('Please fill in all the required fields.');
      return;
    }

    // Set loading state to disable the button
    this.isRequestingQuote = true;
    this._changeDetectorRef.detectChanges();

    this._intakeService.requestQuote(this.quoteRequestForm).subscribe({
      next: (response) => {
        if (!response.success) {
          this._cathyService.error(response.message ?? 'Error requesting quote');
          // Re-enable button on error
          this.isRequestingQuote = false;
          this._changeDetectorRef.detectChanges();
          return;
        }

        // console.log('onSubmit', response.data)
        this.complete.emit();
        this.goToStep(IntakeFormStep.Confirmation);
        // Keep loading state true since we're navigating to confirmation
        // Don't re-enable the button as user will be on confirmation step
        this._changeDetectorRef.detectChanges();
      },
      error: (error) => {
        console.error('Quote request error:', error);
        this._cathyService.error('An error occurred while requesting your quote. Please try again.');
        // Re-enable button on error
        this.isRequestingQuote = false;
        this._changeDetectorRef.detectChanges();
      },
    });
  }

  /**
   * Returns the owners FormArray.
   */
  get owners(): FormArray {
    return this.quoteRequestForm.controls.requestDetails.controls.owners;
  }

  onCompanyNumberOwnersChanged(value: number) {
    const ownersArray = this.owners; // get the FormArray
    const currentCount = ownersArray.length;

    if (value > currentCount) {
      // Add owners until we have the desired count
      for (let i = currentCount; i < value; i++) {
        ownersArray.push(this.createOwnerGroup());
      }
    } else if (value < currentCount) {
      // Remove owners from the end until we reach the desired count
      for (let i = currentCount; i > value; i--) {
        ownersArray.removeAt(i - 1);
      }
    }
    this.quoteRequestForm.controls.requestDetails.controls.company_number_owners.setValue(value);
    ownersArray.controls.forEach((owner) => {
      owner.get('owner_percent_owned')?.setValue(100 / value);
    });
  }

  /**
   * Updates SSN validation for all existing owner form groups based on current state bond requirements
   */
  refreshOwnerSsnValidation(): void {
    const ownersArray = this.owners;
    const stateBond = this._intakeService.selectedStateBond();
    const requiresCreditCheck = stateBond && stateBond !== null && requiresCredit(stateBond);

    ownersArray.controls.forEach((ownerGroup) => {
      const ssnControl = ownerGroup.get('owner_ssn');
      if (ssnControl) {
        if (requiresCreditCheck) {
          // Add required and pattern validators for credit check
          ssnControl.setValidators([Validators.required, Validators.pattern('^\\d{3}-?\\d{2}-?\\d{4}$')]);
          ssnControl.enable();
        } else {
          // Remove all validators when credit check is not required
          ssnControl.clearValidators();
          ssnControl.disable();
        }
        ssnControl.updateValueAndValidity();
      }
    });
  }

  /**
   * Adds a new owner form group to the owners form array.
   */
  addOwner(): void {
    const ownersArray = this.owners;
    ownersArray.push(this.createOwnerGroup());
  }

  /**
   * Removes an owner FormGroup from the owners FormArray by index.
   */
  removeOwner(index: number): void {
    this.owners.removeAt(index);
  }

  // TODO: move this to a shared library where createQuoteRequestForm is defined
  /**
   * Creates and returns an owner form group.
   */
  createOwnerGroup(): FormGroup<CIPXQuoteRequestIntakeFormOwner> {
    const ownerSsnValidators = [];
    const stateBond = this._intakeService.selectedStateBond();
    if (stateBond && stateBond !== null && requiresCredit(stateBond)) {
      // Only add validators when credit check is required
      ownerSsnValidators.push(Validators.required);
      ownerSsnValidators.push(Validators.pattern('^\\d{3}-?\\d{2}-?\\d{4}$'));
    }
    const fg = new FormGroup<CIPXQuoteRequestIntakeFormOwner>({
      owner_first_name: new FormControl('', Validators.required),
      owner_middle_name: new FormControl(''),
      owner_last_name: new FormControl('', Validators.required),
      owner_email: new FormControl('', [
        // Validators.required,
        Validators.email,
      ]),
      owner_phone: new FormControl(''),
      owner_ssn: new FormControl('', ownerSsnValidators),
      owner_marital_status: new FormControl(null),
      owner_dob: new FormControl(''), // Consider using a date picker with proper formatting
      owner_years_experience: new FormControl(null, [Validators.min(0), Validators.required]),
      owner_percent_owned: new FormControl(100, [Validators.min(0), Validators.max(100)]),
      owner_title: new FormControl(''),
      owner_address_street_1: new FormControl('', Validators.required),
      owner_address_street_2: new FormControl(''),
      owner_address_city: new FormControl('', Validators.required),
      owner_address_state_abbreviation: new FormControl('', [
        Validators.minLength(2),
        Validators.maxLength(2),
        Validators.required,
      ]),
      owner_address_zip: new FormControl('', [Validators.pattern('^[0-9]{5}(-[0-9]{4})?$'), Validators.required]),
    });

    if (isCIPXQuoteRequestIntakeFormStateBond(stateBond) && requiresCredit(stateBond) === false) {
      fg.controls.owner_ssn.disable();
    }

    return fg;
  }

  // Footer action methods
  private resetValidationState(control: AbstractControl | null | undefined): void {
    if (!control) return;
    control.markAsPristine();
    control.markAsUntouched();
    control.updateValueAndValidity({ emitEvent: false });

    if ((control as unknown as { controls?: unknown }).controls) {
      const groupOrArray = control as unknown as { controls?: Record<string, AbstractControl> | AbstractControl[] };
      const childControls: AbstractControl[] = Array.isArray(groupOrArray.controls)
        ? groupOrArray.controls
        : Object.values(groupOrArray.controls ?? ({} as Record<string, AbstractControl>));
      childControls.forEach((c) => this.resetValidationState(c));
    }
  }

  canProceed(): boolean {
    const currentStep = this.currentStep();

    if (
      IntakeFormStep.PrincipalSelection === currentStep &&
      (this._intakeService.principalsLoading() || this._intakeService.principalDetailLoading())
    ) {
      return false;
    }

    // If we're requesting a quote, disable the button
    if (currentStep === IntakeFormStep.Notifications && this.isRequestingQuote) {
      return false;
    }

    const isValid = this._intakeService.validateCurrentStep(
      currentStep,
      this.quoteRequestForm,
      this.selectedStateBond()
    );
    return isValid;
  }

  getActionButtonText(): string {
    switch (this.currentStep()) {
      case IntakeFormStep.PrincipalSelection:
        return this.isPrincipalLoading() ? 'Loading principal information...' : 'Next';
      case IntakeFormStep.BondLookup:
        return 'Next';
      case IntakeFormStep.RequestDetails:
        return 'Next';
      case IntakeFormStep.AdditionalQuestions:
        return 'Next';
      case IntakeFormStep.Notifications:
        return this.isRequestingQuote ? 'Requesting Quote...' : 'Request Quote';
      default:
        return 'Next';
    }
  }

  isPrincipalLoading(): boolean {
    return (
      this.currentStep() === IntakeFormStep.PrincipalSelection &&
      (this._intakeService.principalsLoading() || this._intakeService.principalDetailLoading())
    );
  }

  handlePrimaryAction(): void {
    const currentStep = this.currentStep();

    switch (currentStep) {
      case IntakeFormStep.PrincipalSelection:
        this.validateAndProceedToNext(IntakeFormStep.PrincipalSelection, IntakeFormStep.BondLookup);
        // this.validateAndProceedToNext(IntakeFormStep.PrincipalSelection, IntakeFormStep.Notifications);
        break;
      case IntakeFormStep.BondLookup:
        // console.log('[Next] BondLookup → RequestDetails', this.quoteRequestForm.controls.requestDetails.value);
        this.validateAndProceedToNext(IntakeFormStep.BondLookup, IntakeFormStep.RequestDetails);
        break;
      case IntakeFormStep.RequestDetails:
        // console.log('[Next] RequestDetails → AdditionalQuestions BEFORE', this.quoteRequestForm.controls.requestDetails.value);
        this.validateAndProceedToNext(IntakeFormStep.RequestDetails, IntakeFormStep.AdditionalQuestions);
        // Log right after validate to catch mutations during step change
        // setTimeout(() => {
        //   console.log('[Next] RequestDetails → AdditionalQuestions AFTER', this.quoteRequestForm.controls.requestDetails.value);
        // }, 0);
        break;
      case IntakeFormStep.AdditionalQuestions:
        // console.log('[Submit] AdditionalQuestions submit; requestDetails snapshot', this.quoteRequestForm.controls.requestDetails.value);
        this.validateAndProceedToNext(IntakeFormStep.AdditionalQuestions, IntakeFormStep.Notifications);
        break;
      case IntakeFormStep.Notifications: {
        // Validate notifications again before submit
        const ok = this._intakeService.validateCurrentStep(
          IntakeFormStep.Notifications,
          this.quoteRequestForm,
          this.selectedStateBond()
        );
        if (!ok) {
          this._cathyService.error('Please select at least one notification recipient and provide a value.');
          return;
        }
        this.onSubmit();
        // this.validateAndProceedToNext(IntakeFormStep.AdditionalQuestions, IntakeFormStep.Confirmation);
        break;
      }
    }
  }

  onCancel(): void {
    // Emit a cancel event or navigate away
    this.cancelRequestQuote.emit();
  }

  onConfirmationCloseRequested(): void {
    // Emit close request from confirmation step
    this.confirmationCloseRequested.emit();
  }

  scrollToTop(): void {
    // Use setTimeout to ensure the view is updated after step changes
    setTimeout(() => {
      if (this.intakeFormScroll?.nativeElement) {
        this.intakeFormScroll.nativeElement.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        this.fallbackScrollToTop();
      }
    }, 50); // Small delay to ensure DOM is updated
  }

  private fallbackScrollToTop(): void {
    // Fallback: try to find the scrollable element by class
    const scrollableElement = document.querySelector('.cipx-scrollable-content');
    if (scrollableElement) {
      scrollableElement.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }
}
